(ns pfo.notify
  (:require [taoensso.timbre :as timbre]
            [pfo.api :as api]
            [promesa.core :as p]))

(defonce ^:private handlers (atom {}))
(defonce ^:private event-source (atom nil))
(defonce ^:private retry-count (atom 0))
(defonce ^:private retry-timer (atom nil))

(defn register-handler
  "Register a handler function for events of the given type.
   The handler receives the parsed payload map."
  [event-type handler-fn]
  (swap! handlers assoc event-type handler-fn))

(defn- dispatch-event [data]
  (let [parsed (js->clj (js/JSON.parse data) :keywordize-keys true)
        event-type (:type parsed)]
    (when-not (= event-type "keepalive")
      (when-let [handler (get @handlers event-type)]
        (handler (:payload parsed))))))

(defn- listen-url [tok]
  (str js/API_BASE_URL "/notify/listen?token=" tok))

(declare connect! schedule-retry)

(defn- open-sse [tok]
  (when-let [es @event-source]
    (.close es)
    (reset! event-source nil))
  (let [es (js/EventSource. (listen-url tok))]
    (reset! event-source es)
    (set! (.-onmessage es)
          (fn [event]
            (dispatch-event (.-data event))
            ;; Reset retry counter once connection is stable (10s without error).
            (js/setTimeout
             (fn []
               (when (and @event-source
                          (= (.-readyState @event-source) js/EventSource.OPEN))
                 (reset! retry-count 0)))
             10000)))
    (set! (.-onerror es)
          (fn [_err]
            (timbre/warn "notify: connection error")
            (when-let [es @event-source]
              (.close es)
              (reset! event-source nil))
            (schedule-retry)))))

(defn- schedule-retry []
  ;; A retry scheduled before disconnect! could not cancel it must not leave
  ;; two chains running; replace, never stack (#3058).
  (when-let [timer @retry-timer]
    (js/clearTimeout timer))
  (let [max-delay-ms 30000
        stable-base  1000
        next-retry   (min max-delay-ms
                          (* stable-base (js/Math.pow 1.1 @retry-count)))]
    (timbre/info (str "notify: reconnecting in " (/ next-retry 1000) "s (retry " (inc @retry-count) ")"))
    (reset! retry-timer
            (js/setTimeout
             (fn []
               (reset! retry-timer nil)
               (swap! retry-count inc)
               ;; The session may have ended since this timer was scheduled;
               ;; without one, every retry is a guaranteed 401 (#3058). The
               ;; next login mounts the main layout, which calls connect!.
               (when (= :logged-in (api/get-auth-info :auth-state))
                 (connect!)))
             next-retry))))

(defn connect!
  "Obtain a fresh notify token and open the SSE connection.
   Safe to call multiple times — closes any existing connection first."
  []
  ;; A 401 here goes through the global default-fail on purpose: it is the
  ;; only periodic background call, so it doubles as the idle-session logout.
  ;; The :logged-in guard in default-fail keeps it from wiping the MFA
  ;; screens (#3058).
  (-> (api/ajaxp "/notify/init" {:method :post :disable-loadin-counter true})
      (p/then (fn [response]
                ;; disconnect! cannot abort an in-flight init; do not open a
                ;; connection nobody owns after logout (#3058).
                (when (= :logged-in (api/get-auth-info :auth-state))
                  (open-sse (:token response)))))
      (p/catch (fn [err]
                 (timbre/warn "notify: failed to get token" err)
                 (schedule-retry)))))

(defn disconnect!
  "Close the SSE connection and cancel any pending reconnect."
  []
  (when-let [timer @retry-timer]
    (js/clearTimeout timer)
    (reset! retry-timer nil))
  (when-let [es @event-source]
    (.close es)
    (reset! event-source nil))
  (reset! retry-count 0))
